import React, { useState } from 'react';
import { Avatar, Box, Button, Menu, MenuItem, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { truncateAddress, useWallet } from '@aptos-labs/wallet-adapter-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import { useUserContext } from 'context/userContext';
import useLogout from '../hooks/useLogout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { copyAccountAddress } from 'utils/clipboard';

export default function AccountButton() {
	const theme = useTheme();
	const { walletAddress, isLoggedIn } = useUserContext();
	const { mutate: logout } = useLogout();
	const { wallet } = useWallet();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const isDarkMode = theme.palette.mode === 'dark';

	const walletName = () => {
		if (wallet?.name == 'Continue with Google') {
			return 'Google';
		}
		if (wallet?.name == 'Continue with Apple') {
			return 'Apple';
		}
		return wallet?.name;
	};
	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (open) {
			setAnchorEl(null);
		} else {
			setAnchorEl(event.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleDisconnect = () => {
		logout();
		handleClose();
	};

	if (!isLoggedIn || !walletAddress) {
		return null;
	}

	return (
		<>
			<Button
				onClick={handleClick}
				variant="outlined"
				sx={{
					borderRadius: '20px',
					paddingX: 2,
					paddingY: 1,
					textTransform: 'none',
					borderColor: theme.palette.atlas.border.deep,
					color: theme.palette.text.primary,
					backgroundColor: 'transparent',
					'& .MuiButton-endIcon': {
						display: 'inline-flex',
						alignItems: 'center',
					},
					'&:hover': {
						borderColor: theme.palette.divider,
						backgroundColor: theme.palette.action.hover,
					},
				}}
				endIcon={
					<KeyboardArrowDownIcon
						sx={{
							transition: 'transform 0.2s ease-in-out',
							transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
						}}
					/>
				}
			>
				<Box display="flex" alignItems="center" gap={1}>
					{/* Wallet Logo */}
					<Avatar
						sx={{
							width: 24,
							height: 24,
							backgroundColor: 'transparent',
							fontSize: '12px',
							borderRadius: '20px',
						}}
					>
						<img
							src={wallet?.icon}
							alt={walletName() || 'Wallet'}
							style={{
								width: 20,
								height: 20,
								borderRadius: '20px',
							}}
						/>
					</Avatar>

					{/* Address */}
					<Typography
						variant="body2"
						color="textPrimary"
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							fontWeight: 500,
						}}
					>
						{truncateAddress(walletAddress)}{' '}
						{walletAddress && (
							<ContentCopyIcon
								onClick={(event) => {
									event.stopPropagation();
									void copyAccountAddress(walletAddress);
								}}
								sx={{
									color: theme.palette.text.secondary,
									width: 20,
									height: 20,
									marginLeft: 1,
								}}
							/>
						)}
					</Typography>
				</Box>
			</Button>

			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				disableScrollLock={true}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				PaperProps={{
					sx: {
						mt: 1,
						minWidth: 200,
						borderRadius: 2,
						boxShadow: `0px 4px 20px ${theme.palette.atlas.overlay.shadow}`,
						backgroundColor:
							theme.palette.atlas.background.surfaceRaised,
						color: theme.palette.text.primary,
						border: `1px solid ${theme.palette.atlas.border.dark}`,
					},
				}}
				MenuListProps={{
					sx: {
						pb: 0,
					},
				}}
			>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderBottom: `1px solid ${theme.palette.divider}`,
					}}
				>
					<Typography variant="caption" color="textPrimary">
						Connected with {walletName()}
					</Typography>
				</Box>

				<MenuItem
					onClick={handleDisconnect}
					sx={{
						py: 1.5,
						color: 'error.main',
						fontSize: 14,
						'&:hover': {
							backgroundColor: alpha(
								theme.palette.error.main,
								isDarkMode ? 0.2 : 0.08,
							),
						},
					}}
				>
					<LogoutIcon sx={{ mr: 1.5, fontSize: 14 }} />
					Disconnect
				</MenuItem>
			</Menu>
		</>
	);
}
